import React from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const footerLinks = {
  services: [
    { name: "Concierge Care", path: "/services" },
    { name: "Virtual Clinic", path: "/services" },
    { name: "Mental Health", path: "/services" },
    { name: "Chronic Disease", path: "/services" },
    { name: "Preventive Care", path: "/services" }
  ],
  company: [
    { name: "About Us", path: "/about" },
    { name: "Our Doctors", path: "/about" },
    { name: "Pricing", path: "/pricing" },
    { name: "Contact", path: "/contact" }
  ],
  support: [
    { name: "Help Center", path: "/contact" },
    { name: "Privacy Policy", path: "/privacy" },
    { name: "Terms of Service", path: "/terms" },
    { name: "FAQs", path: "/services" }
  ]
};

export default function Footer() {
  return (
    <Box component="footer" sx={{ bgcolor: "text.primary", color: "#fff" }}>
      {/* Newsletter */}
      <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Box className="container-custom" sx={{ py: 6 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 3
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, mb: 1 }}>
                Stay Updated with Health Tips
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
                Join 50,000+ subscribers receiving weekly wellness insights.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, width: { xs: "100%", md: "auto" } }}>
              <TextField
                type="email"
                placeholder="Enter your email"
                variant="outlined"
                size="small"
                sx={{
                  minWidth: { xs: 0, md: 250 },
                  flex: { xs: 1, md: "unset" },
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,0.1)",
                    color: "#fff"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.2)"
                  },
                  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.35)"
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255,255,255,0.5)",
                    opacity: 1
                  }
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: "primary.main",
                  color: "#fff",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "rgba(39,165,153,0.9)" }
                }}
              >
                Subscribe
                <ArrowForwardRoundedIcon sx={{ ml: 1, fontSize: 18 }} />
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main footer */}
      <Box className="container-custom" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6} lg={5}>
            <Box component={Link} to="/" sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none", color: "#fff", mb: 3 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FavoriteRoundedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                Rite
                <Box component="span" sx={{ color: "primary.main" }}>
                  Wellness
                </Box>
              </Typography>
            </Box>

            <Typography sx={{ color: "rgba(255,255,255,0.6)", mb: 3, maxWidth: 420 }}>
              Your trusted partner in holistic health. We provide personalized concierge care, 24/7
              virtual access, and comprehensive wellness programs for the whole family.
            </Typography>

            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Box
                component="a"
                href="tel:+911234567890"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "rgba(255,255,255,0.6)",
                  textDecoration: "none",
                  transition: "color 200ms ease",
                  "&:hover": { color: "primary.main" }
                }}
              >
                <PhoneRoundedIcon sx={{ fontSize: 16 }} />
                <span>+1 (718) 844-8444</span>
              </Box>

              <Box
                component="a"
                href="mailto:care@ritewellness.com"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  color: "rgba(255,255,255,0.6)",
                  textDecoration: "none",
                  transition: "color 200ms ease",
                  "&:hover": { color: "primary.main" }
                }}
              >
                <MailRoundedIcon sx={{ fontSize: 16 }} />
                <span>care@ritewellness.com</span>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "rgba(255,255,255,0.6)" }}>
                <PlaceRoundedIcon sx={{ fontSize: 16 }} />
                <span>Bloomington, Indiana</span>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4} md={2} lg={2}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", mb: 2 }}>Services</Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
              {footerLinks.services.map((l) => (
                <li key={l.name}>
                  <Box component={Link} to={l.path} sx={{ color: "rgba(255,255,255,0.6)", textDecoration: "none", transition: "color 200ms ease", "&:hover": { color: "primary.main" } }}>
                    {l.name}
                  </Box>
                </li>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4} md={2} lg={2}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", mb: 2 }}>Company</Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
              {footerLinks.company.map((l) => (
                <li key={l.name}>
                  <Box component={Link} to={l.path} sx={{ color: "rgba(255,255,255,0.6)", textDecoration: "none", transition: "color 200ms ease", "&:hover": { color: "primary.main" } }}>
                    {l.name}
                  </Box>
                </li>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4} md={2} lg={2}>
            <Typography sx={{ fontWeight: 700, fontSize: "1.125rem", mb: 2 }}>Support</Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 1.25 }}>
              {footerLinks.support.map((l) => (
                <li key={l.name}>
                  <Box component={Link} to={l.path} sx={{ color: "rgba(255,255,255,0.6)", textDecoration: "none", transition: "color 200ms ease", "&:hover": { color: "primary.main" } }}>
                    {l.name}
                  </Box>
                </li>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Bottom */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <Box className="container-custom" sx={{ py: 3, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
            © 2024 Rite Wellness. All rights reserved. Your health, our priority.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {[
              { label: "Facebook", href: "https://facebook.com/ritewellness", icon: <FacebookRoundedIcon sx={{ fontSize: 18 }} /> },
              { label: "Twitter", href: "https://twitter.com/ritewellness", icon: <TwitterIcon sx={{ fontSize: 18 }} /> },
              { label: "Instagram", href: "https://instagram.com/ritewellness", icon: <InstagramIcon sx={{ fontSize: 18 }} /> },
              { label: "LinkedIn", href: "https://linkedin.com/company/ritewellness", icon: <LinkedInIcon sx={{ fontSize: 18 }} /> }
            ].map((s) => (
              <IconButton
                key={s.label}
                component="a"
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.6)",
                  transition: "all 200ms ease",
                  "&:hover": { bgcolor: "primary.main", color: "#fff" }
                }}
              >
                {s.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
